import { Redirect, Tabs } from 'expo-router';
import React, { useEffect } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { ChatFAB } from '@/components/chat/ChatFAB';
import { CustomTabBar } from '@/components/navigation/CustomTabBar';
import { TabBarGradientBlur } from '@/components/navigation/TabBarGradientBlur';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { ChatSessionManager } from '@/lib/chat/ChatSessionManager';
import { useSupabaseAuth } from '@/lib/supabase';
import { summaryScheduler } from '@/lib/services/SummaryScheduler';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import type { SummaryPreferences } from '@/types/home-summary';

export default function TabLayout() {
  const colorScheme = useColorScheme();
  const { session, loading } = useSupabaseAuth();

  useEffect(() => {
    if (!session) {
      summaryScheduler
        .clear()
        .catch(err => console.error('Failed to clear summary scheduler', err));
      return;
    }

    const initializeScheduler = async () => {
      try {
        // Initialize background task
        const initResult = await summaryScheduler.initialize();
        if (initResult.success) {
          console.log('[TabLayout] ✓ Scheduler initialized successfully');
        } else {
          console.error('[TabLayout] ✗ Scheduler initialization failed:', initResult.error);
          return; // Don't proceed if initialization failed
        }

        // Store auth token and userId
        await summaryScheduler.updateAuthToken(
          session.access_token ?? null,
          session.user?.id ?? null
        );
        console.log('[TabLayout] ✓ Auth token and userId stored');

        // Fetch and sync preferences
        const response = await authenticatedFetch.fetch('/api/home-summary/preferences');

        if (response.ok) {
          const data = (await response.json()) as { preferences: SummaryPreferences };
          await summaryScheduler.syncPreferences(data.preferences);
          console.log('[TabLayout] ✓ Preferences synced:', {
            morningEnabled: data.preferences.morningBrief.enabled,
            eveningEnabled: data.preferences.eveningRecap.enabled,
          });
        } else {
          console.error('[TabLayout] ✗ Failed to fetch preferences:', response.status);
        }
      } catch (error) {
        console.error('[TabLayout] ✗ Failed to initialize scheduler:', error);
      }
    };

    initializeScheduler();
  }, [session]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: Colors[colorScheme ?? 'light'].background }}>
        <ActivityIndicator size="large" color={Colors[colorScheme ?? 'light'].tint} />
      </View>
    );
  }

  if (!session) {
    return <Redirect href="/sign-in" />;
  }

  return (
    <>
      {/* Global chat session manager (monitors app background/foreground) */}
      <ChatSessionManager />

      <Tabs
        tabBar={props => <CustomTabBar {...props} />}
        screenOptions={{
          tabBarActiveTintColor: Colors[colorScheme ?? 'light'].tint,
          headerShown: false,
        }}>
        <Tabs.Screen
          name="index"
          options={{
            title: 'Home',
            tabBarLabel: 'Home',
          }}
        />
        <Tabs.Screen
          name="todo"
          options={{
            title: 'To Do',
            tabBarLabel: 'To Do',
          }}
        />
        <Tabs.Screen
          name="calendar"
          options={{
            title: 'Calendar',
            tabBarLabel: 'Calendar',
          }}
        />
        <Tabs.Screen
          name="settings"
          options={{
            title: 'Settings',
            tabBarLabel: 'Settings',
          }}
        />
        <Tabs.Screen
          name="family"
          options={{
            title: 'Fam',
            tabBarLabel: 'Fam',
            href: null,
          }}
        />
      </Tabs>

      {/* Gradient blur overlay above tab bar */}
      <TabBarGradientBlur />

      {/* Global chat FAB (persists across all tabs) */}
      <ChatFAB />
    </>
  );
}
